import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { t } = useTranslation(["auth", "common"]);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await signIn(email.trim(), password);
      const redirectTo = location.state?.from || "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError(t("auth:signIn.invalidCredentials"));
      } else {
        setError(formatApiError(err, t("common:errors.unexpected")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-8" data-testid="login-page">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {t("auth:signIn.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("auth:signIn.subtitle")}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("auth:signIn.emailLabel")}
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              data-testid="login-email-input"
              value={email}
              placeholder={t("auth:signIn.emailPlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("auth:signIn.passwordLabel")}
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              data-testid="login-password-input"
              value={password}
              placeholder={t("auth:signIn.passwordPlaceholder")}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11"
            />
          </div>

          {error && (
            <p
              data-testid="login-error"
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {t("auth:signIn.submit")}
                <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
              </>
            )}
          </Button>
        </form>

        <p className="text-sm text-slate-500">
          {t("auth:signIn.noAccount")}{" "}
          <Link
            to="/signup"
            data-testid="goto-signup-link"
            className="font-medium text-indigo-700 underline-offset-4 hover:underline"
          >
            {t("auth:signIn.createAccount")}
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
